export const WAREHOUSE_HOST_KEY = "warehouse_host"
export const WAREHOUSE_HOST_QUERY_KEY = "warehouse_host"

const INVALID_STORAGE_VALUES = ["null", "undefined"]

function getStorage(type) {
    if (typeof window === "undefined") return null

    try {
        return window[type]
    } catch (error) {
        return null
    }
}

function readStorage(storage, key) {
    if (!storage) return ""

    try {
        return storage.getItem(key) || ""
    } catch (error) {
        return ""
    }
}

function writeStorage(storage, key, value) {
    if (!storage) return false

    try {
        storage.setItem(key, value)
        return true
    } catch (error) {
        return false
    }
}

function removeStorage(storage, key) {
    if (!storage) return

    try {
        storage.removeItem(key)
    } catch (error) {
        // 浏览器禁用存储时忽略清理失败，避免影响页面初始化。
    }
}

export function normalizeWarehouseHost(value) {
    if (typeof value !== "string") return ""

    const host = value.trim()
    if (!host || INVALID_STORAGE_VALUES.includes(host.toLowerCase())) return ""

    try {
        const parsedUrl = new URL(host)
        if (!["http:", "https:"].includes(parsedUrl.protocol)) return ""
        if (parsedUrl.username || parsedUrl.password) return ""
        return parsedUrl.origin
    } catch (error) {
        return ""
    }
}

export function getStoredWarehouseHost() {
    const localStorage = getStorage("localStorage")
    const sessionStorage = getStorage("sessionStorage")
    const rawLocalHost = readStorage(localStorage, WAREHOUSE_HOST_KEY)
    const localHost = normalizeWarehouseHost(rawLocalHost)

    if (localHost) {
        removeStorage(sessionStorage, WAREHOUSE_HOST_KEY)
        return localHost
    }
    if (rawLocalHost) removeStorage(localStorage, WAREHOUSE_HOST_KEY)

    const rawSessionHost = readStorage(sessionStorage, WAREHOUSE_HOST_KEY)
    const legacySessionHost = normalizeWarehouseHost(rawSessionHost)
    if (rawSessionHost && !legacySessionHost) removeStorage(sessionStorage, WAREHOUSE_HOST_KEY)
    if (!legacySessionHost) return ""

    // 兼容旧版本：首次读取后迁移到可跨标签共享的 localStorage。
    if (writeStorage(localStorage, WAREHOUSE_HOST_KEY, legacySessionHost)) {
        removeStorage(sessionStorage, WAREHOUSE_HOST_KEY)
    }

    return legacySessionHost
}

export function getWarehouseHost(defaultHost = "") {
    return getStoredWarehouseHost() || normalizeWarehouseHost(defaultHost)
}

export function setWarehouseHost(value) {
    const host = normalizeWarehouseHost(value)
    if (!host) return ""

    const localStorage = getStorage("localStorage")
    const sessionStorage = getStorage("sessionStorage")
    const localWriteSucceeded = writeStorage(localStorage, WAREHOUSE_HOST_KEY, host)
    const rawLocalHost = readStorage(localStorage, WAREHOUSE_HOST_KEY)
    const storedLocalHost = normalizeWarehouseHost(rawLocalHost)

    // 必须回读确认，避免写入失败后继续沿用 localStorage 中的旧商家。
    if (storedLocalHost === host) {
        removeStorage(sessionStorage, WAREHOUSE_HOST_KEY)
        return host
    }

    // localStorage 有旧值或出现“写入成功但无法回读”时，不允许回退到
    // sessionStorage，否则读取仍会优先命中旧商家，造成 token 与商家错配。
    if (rawLocalHost || localWriteSucceeded) return ""

    // localStorage 不可用时保留旧的单标签行为，至少不阻断当前页面。
    return writeStorage(sessionStorage, WAREHOUSE_HOST_KEY, host) ? host : ""
}

export function clearWarehouseHost() {
    removeStorage(getStorage("localStorage"), WAREHOUSE_HOST_KEY)
    removeStorage(getStorage("sessionStorage"), WAREHOUSE_HOST_KEY)
}

/**
 * 读取后台生成链接中的商户域名，并在应用初始化前切换商户上下文。
 * 参数消费后会从地址栏移除，避免用户后续手动切换商户再刷新时被切回。
 */
export function consumeWarehouseHostFromUrl(defaultHost = "") {
    const emptyResult = {
        handled: false,
        applied: false,
        changed: false,
        warehouseHost: ""
    }

    if (typeof window === "undefined") return emptyResult

    let currentUrl
    try {
        currentUrl = new URL(window.location.href)
    } catch (error) {
        return emptyResult
    }

    if (!currentUrl.searchParams.has(WAREHOUSE_HOST_QUERY_KEY)) return emptyResult

    const targetHost = normalizeWarehouseHost(currentUrl.searchParams.get(WAREHOUSE_HOST_QUERY_KEY))
    const currentHost = getWarehouseHost(defaultHost)

    // warehouse_host 只负责本次入口初始化，消费后不继续保留在页面地址中。
    currentUrl.searchParams.delete(WAREHOUSE_HOST_QUERY_KEY)
    try {
        window.history.replaceState(
            window.history.state,
            "",
            `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
        )
    } catch (error) {
        // history 不可用不影响商户上下文初始化。
    }

    if (!targetHost) {
        return Object.assign({}, emptyResult, { handled: true })
    }

    const warehouseHost = setWarehouseHost(targetHost)
    return {
        handled: true,
        applied: warehouseHost === targetHost,
        changed: currentHost !== targetHost,
        warehouseHost
    }
}
